import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import AuthModal from "@/components/AuthModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Shield, Calendar, Plane, AlertTriangle, Check, Star, PawPrint } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const FEATURES = [
  {
    icon: Calendar,
    title: "Smart Timeline",
    desc: "Auto-reminders for vaccines, meds, and grooming. Never miss a beat.",
    bg: "bg-[#FFF9C4]",
    iconColor: "text-[#FF8A65]",
  },
  {
    icon: Shield,
    title: "Digital Pet Passport",
    desc: "All records in one tap-shareable place. Vets and boarders will love you.",
    bg: "bg-[#E8F5E9]",
    iconColor: "text-[#66BB6A]",
  },
  {
    icon: Plane,
    title: "Travel Mode",
    desc: "AI-powered travel prep. Required vaccines, packing list — done in seconds.",
    bg: "bg-[#E3F2FD]",
    iconColor: "text-[#42A5F5]",
  },
  {
    icon: AlertTriangle,
    title: "Emergency Mode",
    desc: "One button. Nearest vet. Pet's full medical summary. Always ready.",
    bg: "bg-[#FFEBEE]",
    iconColor: "text-[#EF5350]",
  },
];

export default function LandingPage() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");

  const openSignIn = () => { setAuthMode("signin"); setAuthOpen(true); };
  const openSignUp = () => { setAuthMode("signup"); setAuthOpen(true); };
  const handleAuthenticated = (u) => { setUser(u); navigate("/dashboard"); };

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  if (user) return null;

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      {/* ── Navigation ─────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FF8A65] flex items-center justify-center">
              <PawPrint size={18} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-['Fredoka_One'] text-xl text-slate-800 tracking-wide">Pet Atlas</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-slate-600 hover:text-[#FF8A65] transition-colors">Features</a>
            <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-[#FF8A65] transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="hero-signin-btn"
              onClick={openSignIn}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors shadow-md hover:-translate-y-0.5 transition-transform"
            >
              {t("common.signIn")}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-12 md:pt-24 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF5E6] border border-[#FFDDB3] mb-6">
              <PawPrint size={14} className="text-[#FF8A65]" />
              <span className="text-xs font-bold text-[#FF8A65] uppercase tracking-wider">{t("landing.tagline")}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 leading-tight mb-6">
              {t("landing.heroTitle")}<br />
              <span className="text-[#FF8A65]">{t("landing.heroTitleAccent")}</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-md">
              {t("landing.heroSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <button
                data-testid="landing-cta-btn"
                onClick={openSignUp}
                className="flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#FF8A65] text-white font-bold text-base hover:bg-[#FF7043] transition-all shadow-lg hover:-translate-y-1 hover:shadow-xl"
              >
                <PawPrint size={18} strokeWidth={2.5} />
                {t("landing.ctaPrimary")}
              </button>
              <p className="text-sm text-slate-500 mt-3 sm:mt-0 sm:self-center">No credit card needed</p>
            </div>
          </div>
          <div className="relative animate-fade-in delay-200 hidden md:block">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#FFD0BC]/40 to-[#C8E6C9]/30 rounded-3xl blur-2xl" />
            <img
              src="https://images.pexels.com/photos/7876348/pexels-photo-7876348.jpeg"
              alt="Happy pet owner"
              className="relative w-full h-96 object-cover rounded-3xl shadow-2xl"
            />
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-3 border border-slate-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                <Check size={18} className="text-[#66BB6A]" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-800">Rabies Vaccine</p>
                <p className="text-xs text-slate-500">Reminder set for Max</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mt-16 max-w-lg">
          {[["5,000+", "Pets managed"], ["99.9%", "Reminder accuracy"], ["Zero", "Missed vaccines"]].map(([num, label]) => (
            <div key={label} className="text-center">
              <div className="text-2xl font-black text-[#FF8A65] font-['Nunito']">{num}</div>
              <div className="text-xs text-slate-500 font-medium mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────── */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 font-['Nunito'] mb-3">Built for peace of mind</h2>
          <p className="text-slate-500 max-w-md mx-auto">Everything a responsible pet owner needs — in one warm, friendly app.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`${f.bg} rounded-3xl p-6 border border-white/60 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 animate-fade-in-up`}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="w-12 h-12 rounded-2xl bg-white/70 flex items-center justify-center mb-4 shadow-sm">
                <f.icon size={22} className={f.iconColor} strokeWidth={2.5} />
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-2 font-['Nunito']">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Travel Mode Feature Highlight ──────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 sm:p-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E3F2FD] mb-4">
                <Plane size={13} className="text-[#42A5F5]" />
                <span className="text-xs font-bold text-[#42A5F5]">PREMIUM FEATURE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 font-['Nunito'] mb-4">Travel with your pet, stress-free</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">Enter your destination and our AI generates a complete travel guide — required vaccines, airline rules, and a packing checklist.</p>
              <button onClick={openSignUp} className="px-6 py-3 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-colors">
                Try Travel Mode
              </button>
            </div>
            <div className="relative min-h-[240px]">
              <img
                src="https://images.pexels.com/photos/6716737/pexels-photo-6716737.jpeg"
                alt="Dog ready to travel"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social proof ───────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-1 mb-3">
            {[0,1,2,3,4].map((n) => (
              <Star key={n} size={16} fill="#FFB300" stroke="#FFB300" />
            ))}
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 font-['Nunito'] mb-3">
            Loved by pet parents everywhere
          </h2>
          <p className="text-slate-500">Real stories from early users.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            {
              quote: "Finally I don't miss Max's booster shots. The reminders just... show up. Peace of mind I didn't know I was missing.",
              name: "Sarah M.",
              role: "Dog mom of two retrievers",
              avatar: "SM",
              bg: "bg-[#FFF5E6]",
              fg: "text-[#FF8A65]",
            },
            {
              quote: "Took Luna to Portugal last summer. Travel Mode told me exactly which vaccines I needed months in advance. Saved me a trip to the vet and a panic attack.",
              name: "Daniel K.",
              role: "Weekend traveler, one very spoiled cat",
              avatar: "DK",
              bg: "bg-[#E8F5E9]",
              fg: "text-[#388E3C]",
            },
            {
              quote: "The emergency card is the feature I pray I never need — but when the dog-sitter called at 11pm, one tap and she had everything. Worth the sub alone.",
              name: "Priya R.",
              role: "Vet tech + three rescues",
              avatar: "PR",
              bg: "bg-[#E3F2FD]",
              fg: "text-[#1565C0]",
            },
          ].map((t) => (
            <div
              key={t.name}
              className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 flex flex-col animate-fade-in-up"
              data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[0,1,2,3,4].map((n) => (
                  <Star key={n} size={13} fill="#FFB300" stroke="#FFB300" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-6 flex-1">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${t.bg} ${t.fg} flex items-center justify-center font-bold text-sm`}>
                  {t.avatar}
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────── */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 font-['Nunito'] mb-3">Simple, honest pricing</h2>
          <p className="text-slate-500">You're paying for peace of mind, not features.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
            <h3 className="text-xl font-extrabold text-slate-800 font-['Nunito'] mb-1">Free</h3>
            <div className="text-4xl font-black text-slate-800 font-['Nunito'] mb-1">$0</div>
            <p className="text-sm text-slate-500 mb-6">Forever free</p>
            <ul className="space-y-3 mb-8">
              {["1 pet profile", "Smart reminders", "Digital passport", "Emergency mode"].map(f => (
                <li key={f} className="flex items-center gap-3 text-sm text-slate-700">
                  <Check size={16} className="text-[#66BB6A] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={openSignUp} className="w-full py-3 rounded-full border-2 border-[#FF8A65] text-[#FF8A65] font-semibold hover:bg-[#FFF5E6] transition-colors">
              Get Started Free
            </button>
          </div>
          {/* Premium */}
          <div className="bg-[#FFF9C4]/40 rounded-3xl border-2 border-[#FFD54F] p-8 shadow-md relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-[#FF8A65] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
              <Star size={11} fill="currentColor" /> Popular
            </div>
            <h3 className="text-xl font-extrabold text-slate-800 font-['Nunito'] mb-1">Premium</h3>
            <div className="text-4xl font-black text-[#FF8A65] font-['Nunito'] mb-1">$7.99</div>
            <p className="text-sm text-slate-500 mb-6">per month</p>
            <ul className="space-y-3 mb-8">
              {["Unlimited pets", "AI Travel Mode", "Emergency vet sharing", "Cloud backup", "Passport export"].map(f => (
                <li key={f} className="flex items-center gap-3 text-sm text-slate-700">
                  <Check size={16} className="text-[#FF8A65] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={openSignUp} className="w-full py-3 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-colors shadow-md">
              Start Premium Trial
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <PawPrint size={16} className="text-[#FF8A65]" />
            <span className="font-['Fredoka_One'] text-slate-700">Pet Atlas</span>
          </div>
          <div className="flex items-center gap-6 text-xs">
            <a href="/privacy" data-testid="footer-privacy-link" className="text-slate-500 hover:text-[#FF8A65] transition-colors font-medium">Privacy</a>
            <a href="/terms" data-testid="footer-terms-link" className="text-slate-500 hover:text-[#FF8A65] transition-colors font-medium">Terms</a>
            <a href="mailto:support@petatlas.app" className="text-slate-500 hover:text-[#FF8A65] transition-colors font-medium">Contact</a>
          </div>
          <p className="text-xs text-slate-400">© 2026 Pet Atlas. All rights reserved.</p>
        </div>
      </footer>

      <AuthModal
        open={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
        onAuthenticated={handleAuthenticated}
      />
    </div>
  );
}
